import type { ProductRequestType } from "@editor/hooks/useStoreProducts";
import type { ProductRef, ProductRefOrDynamic } from "schemas/product";

import * as React from "react";

import DynamicDataButton from "@common/designSystem/DynamicDataButton";
import {
  arrayMove,
  SortableItem,
  SortableList,
} from "@common/designSystem/SortableList";
import { DynamicDataSelector } from "@editor/components/editor/page/DynamicDataSelector";
import {
  ProductSelectionButton,
  ProductSelectionCombobox,
} from "@editor/components/editor/page/ProductSelectionCombobox";
import DynamicDataValueIndicator from "@editorExtras/DynamicDataValueIndicator";

import twMerge from "@replo/design-system/utils/twMerge";
import { DynamicDataTargetType } from "replo-runtime/shared/dynamicData";
import { isContextRef } from "replo-runtime/store/ReploProduct";

type MultiProductProps = {
  isMultiProducts: true;
  onChange: (value: ProductRef[]) => void;
  selectedProductRef: ProductRef[] | null;
};

type SingleProductProps = {
  isMultiProducts?: false;
  onChange: (value: ProductRef | null) => void;
  selectedProductRef: ProductRefOrDynamic | null;
};

type ProductSelectorSharedProps = {
  excludedDynamicDataKeys?: string[];
  allowDynamicData?: boolean;
  productRequestType?: ProductRequestType;
  isVariantSelectable?: boolean;
  className?: string;
  showDeleteButton?: boolean;
  noDynamicData?: boolean;
  side?: "top" | "bottom" | "left" | "right";
  sideOffset?: number;
  size?: "sm" | "base";
  onRemove?: () => void;
  triggerLayoutClassName?: string;
  placeholderText?: string;
};

type SingleProductSelectorProps = SingleProductProps &
  ProductSelectorSharedProps;

type MultiProductSelectorProps = MultiProductProps & ProductSelectorSharedProps;

export function ProductSelector(
  props: SingleProductSelectorProps,
): React.ReactElement<any, any> | null;
export function ProductSelector(
  props: MultiProductSelectorProps,
): React.ReactElement<any, any> | null;

export function ProductSelector({
  selectedProductRef,
  allowDynamicData = true,
  onChange,
  isMultiProducts,
  isVariantSelectable = false,
  className,
  side = "bottom",
  sideOffset = 4,
  size = "base",
  onRemove,
  triggerLayoutClassName,
  placeholderText,
}: ProductSelectorSharedProps & (MultiProductProps | SingleProductProps)) {
  if (
    !isMultiProducts &&
    selectedProductRef &&
    isContextRef(selectedProductRef)
  ) {
    return (
      <DynamicDataSelector
        targetType={
          isVariantSelectable
            ? DynamicDataTargetType.PRODUCT_VARIANT
            : DynamicDataTargetType.PRODUCT
        }
        initialPath={selectedProductRef.ref.split(".")}
        side="left"
        sideOffset={12}
        trigger={
          <DynamicDataValueIndicator
            type="other"
            templateValue={`{{${selectedProductRef.ref}}}`}
            onRemove={() => {
              onChange(null);
            }}
          />
        }
        onChange={(value) => {
          (onChange as (value: ProductRefOrDynamic) => void)(value);
        }}
      />
    );
  }

  return (
    <div
      className={twMerge(
        "flex flex-row items-center justify-between gap-2",
        className,
      )}
    >
      {isMultiProducts ? (
        <div className="flex-1">
          <SortableList
            onReorderEnd={({ oldIndex, newIndex }) => {
              const reorderedList = arrayMove(
                (selectedProductRef ?? []) as ProductRef[],
                oldIndex,
                newIndex,
              );
              onChange(reorderedList);
            }}
            withDragHandle
          >
            {((selectedProductRef ?? []) as ProductRef[]).map(
              (productRef, index) => (
                <SortableItem
                  key={productRef?.id ?? `undefined-${index}`}
                  id={productRef?.id ?? `undefined-${index}`}
                >
                  <ProductSelectionCombobox
                    value={selectedProductRef ?? []}
                    onChange={onChange}
                    isMultiselect
                    side={side}
                    sideOffset={sideOffset}
                    trigger={
                      <ProductSelectionButton
                        size={size}
                        title={productRef.title ?? "Edit Products"}
                        // Prevent drag from triggering click
                        onMouseDown={(e) => e.stopPropagation()}
                        onRemove={() => {
                          const updatedProducts = [
                            ...(selectedProductRef ?? []),
                          ];
                          updatedProducts.splice(index, 1);
                          onChange(updatedProducts);
                        }}
                        layoutClassName={triggerLayoutClassName}
                      />
                    }
                  />
                </SortableItem>
              ),
            )}
          </SortableList>
          <ProductSelectionCombobox
            value={selectedProductRef ?? []}
            onChange={onChange}
            isMultiselect
            side={side}
            sideOffset={sideOffset}
            trigger={
              <ProductSelectionButton
                size={size}
                title="Edit Products"
                allowDynamicData
                isPlaceholder
                layoutClassName={twMerge(
                  "mt-2 border-dashed",
                  triggerLayoutClassName,
                )}
              />
            }
          />
        </div>
      ) : (
        <ProductSelectionCombobox
          size={size}
          // Note (Noah, 2025-04-02): Typescript doesn't understand that this must not
          // be dynamic, even though we did a check for isContextRef above
          value={selectedProductRef as ProductRef | null}
          onChange={onChange}
          side={side}
          sideOffset={sideOffset}
          onRemove={onRemove}
          triggerLayoutClassName={triggerLayoutClassName}
          placeholderText={placeholderText}
          allowDynamicData={allowDynamicData}
        />
      )}
      {allowDynamicData && (
        <DynamicDataSelector
          side="left"
          sideOffset={222}
          targetType={
            isVariantSelectable
              ? DynamicDataTargetType.PRODUCT_VARIANT
              : DynamicDataTargetType.PRODUCT
          }
          trigger={<DynamicDataButton />}
          onChange={(value: any) => {
            onChange(value);
          }}
        />
      )}
    </div>
  );
}
